/**
 * Following a background job by short polling (PLAN.md decision 3.2).
 *
 * Every few seconds the job is asked for with the last ETag; an unchanged job answers 304 with no
 * body, so an idle poll is nearly free. Resolves with the finished job.
 */

import { api, type Schemas } from "./client";

export type JobView = Schemas["JobView"];

const FINISHED: ReadonlySet<JobView["status"]> = new Set(["done", "failed", "cancelled"]);

export function isFinished(job: JobView): boolean {
  return FINISHED.has(job.status);
}

export async function followJob(
  jobId: string,
  onUpdate: (job: JobView) => void,
  { intervalMs = 3000, signal }: { intervalMs?: number; signal?: AbortSignal } = {},
): Promise<JobView> {
  let etag: string | undefined;
  let latest: JobView | undefined;

  for (;;) {
    const { data, error, response } = await api.GET("/api/v1/jobs/{job_id}", {
      params: { path: { job_id: jobId }, header: etag ? { "if-none-match": etag } : {} },
      signal,
    });
    if (response.status === 304 && latest) {
      // unchanged since the last poll
    } else if (data) {
      latest = data;
      etag = response.headers.get("ETag") ?? undefined;
      onUpdate(data);
    } else {
      throw new Error(`could not follow job ${jobId}: ${response.status} ${JSON.stringify(error)}`);
    }
    if (isFinished(latest)) return latest;
    await sleep(intervalMs, signal);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      reject(signal.reason);
    });
  });
}
